import { SuggestionCard } from "@/components/analysis/suggestion-card";
import {
  type QueueResponse,
  type QueueSuggestion,
  SuggestionSource,
} from "@/components/analysis/suggestion-source";
import { Button } from "@/components/ui/button";
import {
  type ApplyState,
  type Suggestion,
  approveSuggestion,
  persistRejection,
} from "@/lib/analysis";
import { api } from "@/lib/api";
import { Link, Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * `/suggestions/work-items/review` — **one suggestion at a time** (admin-only).
 *
 * The same pending work-item suggestions as the queue on
 * `/suggestions/work-items`, but presented as a triage run: exactly one card is
 * on screen, the admin edits it if needed and approves or rejects it, and the
 * next pending suggestion loads by itself. The point is that nothing is
 * skipped by accident and there's no scrolling back and forth — you work the
 * pile front to back until it's empty.
 *
 * Approving applies the suggestion through the very same path as the queue
 * (`approveSuggestion`), so a decision made here is identical to one made
 * there. A failed apply keeps you on the card — the run only moves on once the
 * suggestion is actually decided. Skip leaves it pending for later.
 *
 * E2E behaviour contract lives in `e2e/suggestions/suggestions-review.spec.ts`.
 */

export const Route = createFileRoute("/_app/suggestions/work-items_/review")({
  component: ReviewSuggestionsPage,
});

function ReviewSuggestionsPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [items, setItems] = useState<QueueSuggestion[] | null>(null);
  /** How many are pending in total — bigger than this run's batch when the
   *  backlog is longer than one page. */
  const [waiting, setWaiting] = useState(0);
  const [drafts, setDrafts] = useState<Suggestion[]>([]);
  const [index, setIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  /** Apply state of the card on screen — reset on every move. */
  const [applyState, setApplyState] = useState<ApplyState>({ status: "idle" });
  /** Every decision made in this run, keyed by suggestion id, so stepping back
   *  to an already-decided card shows it locked instead of offering it again. */
  const [decisions, setDecisions] = useState<Record<string, ApplyState>>({});
  /** What just happened, kept visible above the next card. */
  const [lastDecision, setLastDecision] = useState<ApplyState | null>(null);

  const load = useCallback(() => {
    setItems(null);
    // A run works through one batch, not the whole backlog — the queue is
    // paged now, and 200 is the largest page the endpoint serves. If more are
    // waiting, finishing this run and starting another picks them up.
    return api
      .get<QueueResponse>("/api/work-item-suggestions?status=pending&pageSize=200")
      .then((res) => {
        setItems(res.suggestions);
        setWaiting(res.total);
        setDrafts(res.suggestions.map((s) => s.payload));
        setIndex(0);
        setApplyState({ status: "idle" });
        setDecisions({});
        setLastDecision(null);
        setError(null);
      })
      .catch(() => setError(t("sug.review.loadError")));
  }, [t]);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load();
  }, [user.globalRole, load]);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    api
      .get<{ projects: Array<{ id: string; name: string }> }>("/api/projects?pageSize=200")
      .then((res) => setProjects(res.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {
        /* the create-item project picker just stays empty */
      });
  }, [user.globalRole]);

  const current = items?.[index];
  const draft = drafts[index] ?? current?.payload;
  const decided = current ? decisions[current.id] : undefined;
  const total = items?.length ?? 0;
  const decidedCount = Object.keys(decisions).length;

  function goTo(next: number) {
    setIndex(next);
    const at = items?.[next];
    setApplyState((at && decisions[at.id]) ?? { status: "idle" });
  }

  function patchDraft(i: number, patch: Partial<Suggestion>) {
    setDrafts((prev) => prev.map((d, j) => (j === i ? ({ ...d, ...patch } as Suggestion) : d)));
  }

  /** Record the decision, surface it above the next card, and move on. */
  function settle(id: string, state: ApplyState) {
    const next = { ...decisions, [id]: state };
    setDecisions(next);
    setLastDecision(state);
    const at = items?.[index + 1];
    setIndex(index + 1);
    setApplyState((at && next[at.id]) ?? { status: "idle" });
  }

  async function approve() {
    if (!current || !draft) return;
    setApplyState({ status: "applying" });
    const next = await approveSuggestion(current.id, draft);
    if (next.status !== "done") {
      // Nothing was decided — stay put so the admin can fix and retry.
      setApplyState(next);
      return;
    }
    settle(current.id, next);
  }

  async function reject(reason?: string) {
    if (!current) return;
    setApplyState({ status: "rejecting" });
    try {
      await persistRejection(current.id, reason);
      settle(current.id, { status: "rejected" });
    } catch {
      setApplyState({ status: "error", message: t("sug.review.rejectError") });
    }
  }

  if (user.globalRole !== "admin") {
    return <Navigate to="/" />;
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold">{t("sug.review.title")}</h1>
        <p className="text-muted-foreground">
          <Trans
            i18nKey="sug.review.intro"
            components={{ link: <Link to="/suggestions/work-items" className="underline" /> }}
          />
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {items && total > 0 && (
        <div className="space-y-1" data-testid="review-progress">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {index < total
                ? t("sug.review.progress", { current: index + 1, total })
                : t("sug.review.reviewed", { total })}
            </span>
            <span>{t("sug.review.decided", { count: decidedCount })}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${total === 0 ? 0 : (decidedCount / total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {lastDecision && (
        <p className="text-sm text-muted-foreground" data-testid="last-decision">
          {lastDecision.status === "rejected"
            ? t("sug.review.previousRejected")
            : t("sug.review.previousApplied", {
                message: lastDecision.message ?? t("sug.review.done"),
              })}
        </p>
      )}

      {items && total === 0 && (
        <div className="space-y-3" data-testid="review-empty">
          <p className="text-sm text-muted-foreground">{t("sug.review.emptyText")}</p>
          <Button type="button" variant="outline" onClick={() => void load()}>
            {t("sug.review.checkAgain")}
          </Button>
        </div>
      )}

      {items && total > 0 && index >= total && (
        <div className="space-y-3" data-testid="review-done">
          <p className="text-sm">
            {t("sug.review.caughtUp", { count: total, decided: decidedCount })}
            {waiting > total ? t("sug.review.moreWaiting", { count: waiting - total }) : ""}
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => void load()}>
              {waiting > total ? t("sug.review.loadNext") : t("sug.review.checkNew")}
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/suggestions/work-items">{t("sug.review.backToQueue")}</Link>
            </Button>
          </div>
        </div>
      )}

      {current && draft && (
        <div className="space-y-2" data-testid="review-suggestion">
          <SuggestionSource analysis={current.analysis} />
          <SuggestionCard
            key={current.id}
            suggestion={draft}
            index={index}
            projects={projects}
            workItemIndex={current.analysis.workItemIndex}
            included={false}
            selectable={false}
            applyState={applyState}
            onToggle={() => {}}
            onPatch={patchDraft}
            onApply={() => void approve()}
            onReject={(reason) => void reject(reason)}
            suggestionId={current.id}
          />

          <div className="flex items-center justify-between pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={index === 0}
              onClick={() => goTo(index - 1)}
              data-testid="review-back"
            >
              {t("sug.review.back")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => goTo(index + 1)}
              data-testid="review-skip"
            >
              {decided ? t("sug.review.next") : t("sug.review.skip")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
